import type { Dispatch } from "react";
import { useMemo, useState } from "react";

import { useTranslation } from "@/lib/i18n";
import "@/components/dialog/dialog.css";
import type {
  ObservationsMapParameterMapLayerConfiguration,
  ParametersObservedMapLayer,
} from "@/lib/api";
import {
  PickListItem,
  PickListModal,
} from "@/features/widget-forms/forms/weather-model-map/modals/pick-list-modal";
import {
  ConfigureLayerModal,
  type LayerDetails,
} from "@/features/widget-forms/forms/map-widget-editor/configure-layer-modal";
import { fmtSubtitle } from "@/features/widget-forms/forms/weather-model-map/modals/map-layer-formatters";

type ObservationsAddMapLayerModalProps = {
  mapLayers: ParametersObservedMapLayer[];
  selectedMapLayerIds: string[];
  onAddMapLayer: Dispatch<ObservationsMapParameterMapLayerConfiguration>;
};

/**
 * Observations map CMS: pick observed map layer + label, same flow as model-map {@link AddMapLayerModal}
 * but catalogued via {@link api.parameters.listObservedMapLayers} and obs-specific i18n keys.
 */
export function ObservationsAddMapLayerModal({
  mapLayers,
  selectedMapLayerIds,
  onAddMapLayer,
}: ObservationsAddMapLayerModalProps) {
  const { t } = useTranslation();
  const items = useMemo(
    () =>
      mapLayers.map((layer): PickListItem => ({
        id: layer.id,
        title: layer.label,
        subtitle: fmtSubtitle(layer.zoom_levels),
      })),
    [mapLayers],
  );

  const [configurationVisible, setConfigurationVisible] = useState(false);
  const [mapLayerId, setMapLayerId] = useState<string>();

  const onPick = (id: string) => {
    setConfigurationVisible(true);
    setMapLayerId(id);
  };

  const mapLayer = mapLayers.find((layer) => layer.id === mapLayerId);

  function onSubmitConfiguration(details: LayerDetails) {
    if (!mapLayerId) return;
    onAddMapLayer({
      parameter_map_layer_id: mapLayerId,
      label: details.label,
      enabled_by_default: true,
    });
    setConfigurationVisible(false);
  }

  return (
    <>
      <PickListModal
        title={t("admin.widgets.fields.observations_map.map_layers.add")}
        description={t("admin.widgets.fields.observations_map.map_layers.add_description")}
        addText={t("admin.widgets.fields.observations_map.map_layers.add")}
        emptyText={t(
          "admin.widgets.fields.observations_map.map_layers.no_layers_available",
        )}
        items={items}
        selected={selectedMapLayerIds}
        onPick={onPick}
      />
      <ConfigureLayerModal
        layer={mapLayer}
        open={configurationVisible}
        onSubmit={onSubmitConfiguration}
        onCancel={() => setConfigurationVisible(false)}
      />
    </>
  );
}
